/**
 * Centralized Environment Configuration
 * All configuration values are loaded from environment variables
 * This ensures a single source of truth for all settings
 */

export const config = {
  // Application Settings
  app: {
    name: import.meta.env.VITE_APP_NAME || 'Oracle ERP Universal UAE E-Invoicing Adapter',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: import.meta.env.VITE_ENV || 'development',
    logLevel: import.meta.env.VITE_LOG_LEVEL || 'info',
  },

  // Server Configuration
  server: {
    port: parseInt(import.meta.env.VITE_PORT || '5173'),
    host: import.meta.env.VITE_HOST || 'localhost',
  },

  // API Configuration
  api: {
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '5000'),
  },

  // Adapter Ports (for reference and documentation)
  adapters: {
    ebs: {
      port: parseInt(import.meta.env.VITE_EBS_ADAPTER_PORT || '3001'),
      name: 'Oracle EBS',
      type: 'EBS',
    },
    fusion: {
      port: parseInt(import.meta.env.VITE_FUSION_ADAPTER_PORT || '3002'),
      name: 'Oracle Fusion',
      type: 'Fusion',
    },
    netsuite: {
      port: parseInt(import.meta.env.VITE_NETSUITE_ADAPTER_PORT || '3003'),
      name: 'NetSuite',
      type: 'NetSuite',
    },
    jde: {
      port: parseInt(import.meta.env.VITE_JDE_ADAPTER_PORT || '3004'),
      name: 'JD Edwards',
      type: 'JDE',
    },
    peoplesoft: {
      port: parseInt(import.meta.env.VITE_PEOPLESOFT_ADAPTER_PORT || '3005'),
      name: 'PeopleSoft',
      type: 'PeopleSoft',
    },
  },

  // Feature Flags
  features: {
    analytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
    debug: import.meta.env.VITE_ENABLE_DEBUG === 'true',
    mockData: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true',
  },

  // Derived URLs
  urls: {
    api: import.meta.env.VITE_API_URL || 'http://localhost:3000',
    ebsAdapter: `http://localhost:${parseInt(import.meta.env.VITE_EBS_ADAPTER_PORT || '3001')}`,
    fusionAdapter: `http://localhost:${parseInt(import.meta.env.VITE_FUSION_ADAPTER_PORT || '3002')}`,
    netsuiteAdapter: `http://localhost:${parseInt(import.meta.env.VITE_NETSUITE_ADAPTER_PORT || '3003')}`,
    jdeAdapter: `http://localhost:${parseInt(import.meta.env.VITE_JDE_ADAPTER_PORT || '3004')}`,
    peoplesoftAdapter: `http://localhost:${parseInt(import.meta.env.VITE_PEOPLESOFT_ADAPTER_PORT || '3005')}`,
  },

  // Environment Checks
  isDevelopment: import.meta.env.VITE_ENV === 'development',
  isStaging: import.meta.env.VITE_ENV === 'staging',
  isProduction: import.meta.env.VITE_ENV === 'production',
};

/**
 * Get adapter URL by type
 */
export function getAdapterUrl(adapterType: string): string {
  const type = adapterType.toLowerCase();
  switch (type) {
    case 'ebs':
      return config.urls.ebsAdapter;
    case 'fusion':
      return config.urls.fusionAdapter;
    case 'netsuite':
      return config.urls.netsuiteAdapter;
    case 'jde':
      return config.urls.jdeAdapter;
    case 'peoplesoft':
      return config.urls.peoplesoftAdapter;
    default:
      return config.api.baseURL;
  }
}

/**
 * Log configuration (only in development/debug mode)
 */
export function logConfiguration(): void {
  if (config.features.debug) {
    console.group('🔧 Application Configuration');
    console.log('App:', config.app);
    console.log('Server:', config.server);
    console.log('API:', config.api);
    console.log('Adapters:', config.adapters);
    console.log('Features:', config.features);
    console.log('URLs:', config.urls);
    console.groupEnd();
  }
}

export default config;
